import { Button, PageTitle, SelectOption, ToastLevel, useIntl, useToast } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { FC, ReactNode, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { getFilteredSiteOptions } from "../stock-allocation/stock-allocation-modal/stock-allocation-modal-helper"
import { getAllocatedSensors } from "../stock-managment/stock-management-helper"
import { SDASensor } from "features/iot/stock-management/models/sensor-dispatch-allocation"
import DispatchedSensorsSection from "./dispatch-view-components/dispatched-sensors-section"
import DispatchDetailsSection from "./dispatch-view-components/dispatch-details-section"
import DispatchSubmitModal from "./dispatch-submit-modal"
import sensorBatchStore from "features/iot/sensor-batch/stores/sensor-batch-store"
import customerStore from "features/organizations/customers/store/customer-store"
import { itemViewSeparatorStyles } from "components/common-style/common-style"

const DispatchView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const { partNumber, customerName, siteName } = useParams()
    const [listOfCustomers, setListOfCustomers] = useState<SelectOption[]>([])
    const [listOfSites, setListOfSites] = useState<SelectOption[]>([])
    const [pickedPartNumber, setPickedPartNumber] = useState<SelectOption>()
    const [pickedCustomer, setPickedCustomer] = useState<SelectOption>()
    const [pickedSite, setPickedSite] = useState<SelectOption>()
    const [quantity, setQuantity] = useState<number>(0)
    const [listOfSensors, setListOfSensors] = useState<SDASensor[]>([])
    const [dispatchDate, setDispatchDate] = useState<Date>(new Date())
    const [isDispatchSubmitModalOpen, setIsDispatchSubmitModalOpen] = useState<boolean>(false)
    const { setMessage } = useToast()
    const { customers } = customerStore
    const { sensorBatches, sensorsMap } = sensorBatchStore

    // Setting List of Customers
    useEffect(() => {
        const customerOptions = customers.map(c => ({
            text: c.name,
            value: c.id,
        }))
        setListOfCustomers(customerOptions)
    }, [customers])

    useEffect(() => {
        const customerOption = listOfCustomers.find(c => c.text.toLowerCase() === customerName?.toLowerCase())
        if (customerOption && customerOption.value !== pickedCustomer?.value) {
            setPickedCustomer(customerOption)
        }
    }, [customerName, listOfCustomers])

    useEffect(() => {
        const siteOption = listOfSites.find(s => s.text.toLowerCase() === siteName?.toLowerCase())
        if (siteOption && siteOption.value !== pickedSite?.value) {
            setPickedSite(siteOption)
        }
    }, [siteName, listOfSites])

    // Setting Allocated Sensors
    useEffect(() => {
        if (pickedCustomer?.value && pickedPartNumber?.text && pickedSite?.text) {
            // Fetch allocated sensors based on the part number.
            const allocatedSensors: SDASensor[] = getAllocatedSensors(pickedPartNumber.text)

            // Filter the allocatedSensors to include only those whose IDs are in matchedSensorIds.
            const filteredData = allocatedSensors.filter(
                sensor => sensor.customerId === pickedCustomer.value && (sensor.siteId || undefined) === (pickedSite?.value || undefined),
            )
            setListOfSensors(filteredData)
            setQuantity(filteredData.length)
        }
    }, [pickedCustomer, pickedPartNumber, pickedSite, sensorBatches, sensorsMap])

    useEffect(() => {
        if (!pickedCustomer?.value) {
            setListOfSites([])
            return
        }
        const findCustomer = customers.find(s => s.id === pickedCustomer.value)
        const filteredSiteOptions = getFilteredSiteOptions(findCustomer?.sites)
        setListOfSites(filteredSiteOptions)
        setPickedSite({ text: "Unknown", value: "" })
    }, [pickedCustomer])

    const modalTitle = formatMessage("dispatch.title")

    const sensorsActionsHandlers = {
        submit: () => {
            if (listOfSensors.length < 0 || dispatchDate == null) {
                setMessage("No sesnors or dispatch Date is empty!", ToastLevel.SUCCESS, 4000)
                return
            }
            setIsDispatchSubmitModalOpen(true)
        },
    }

    const rightPageButtons: ReactNode = (
        <>
            <Button icon='icon-submitted-16' labelPosition='right' onClick={sensorsActionsHandlers.submit} primary>
                {formatMessage("dispatch.submit")}
            </Button>
        </>
    )

    return (
        <>
            <PageTitle title={modalTitle} extraItems={rightPageButtons} />
            <DispatchDetailsSection
                dispatchDate={dispatchDate}
                listOfCustomers={listOfCustomers}
                listOfSites={listOfSites}
                partNumber={partNumber}
                pickedCustomer={pickedCustomer}
                pickedPartNumber={pickedPartNumber}
                pickedSite={pickedSite}
                quantity={quantity}
                setDispatchDate={setDispatchDate}
                setListOfSensors={setListOfSensors}
                setPickedCustomer={setPickedCustomer}
                setPickedPartNumber={setPickedPartNumber}
                setPickedSite={setPickedSite}
            />
            <div style={itemViewSeparatorStyles}></div>
            {
                <DispatchedSensorsSection
                    listOfSensors={listOfSensors}
                    partNumber={partNumber ?? ""}
                    dispatchDate={dispatchDate}
                    pickedCustomer={pickedCustomer}
                    pickedSite={pickedSite}
                />
            }
            {isDispatchSubmitModalOpen && (
                <DispatchSubmitModal
                    partNumber={partNumber ?? ""}
                    isOpen={isDispatchSubmitModalOpen}
                    setIsOpen={setIsDispatchSubmitModalOpen}
                    dispatchDate={dispatchDate}
                    listOfSensors={listOfSensors}
                    customerId={pickedCustomer?.value ?? ""}
                    siteId={pickedSite?.value}
                />
            )}
        </>
    )
})

export default DispatchView
